import {
  WBBox,
  WBButton,
  WBDialog,
  WBDialogContent,
  WBFlex,
  WBForm,
  WBIcon,
  WBIconButton,
  WBLink,
  WBMenuItem,
  WBSelect,
  WBTable,
  WBTableBody,
  WBTableCell,
  WBTableContainer,
  WBTableHead,
  WBTableRow,
  WBTypography,
} from '@admiin-com/ds-web';
import {
  ButtonGroup,
  DialogTitle,
  FormControl,
  Paper,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ErrorHandler from '../../components/ErrorHandler/ErrorHandler';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  createClientBulkUpload as CREATE_CLIENT_BULK_UPLOAD,
  CreateClientBulkImportInput,
} from '@admiin-com/ds-graphql';
import { CSGetSelectedEntityId as GET_SELECTED_ENTITY_ID } from '@admiin-com/ds-graphql';
import { useCsvClientValidation } from './useCsvClientValidation';

export interface MapCSVClientFieldsProps {
  file?: File;
  fileKey: string;
  onSubmitted?: () => void;
}

export interface CSVMapClientFormData {
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  mobile: string;
  phone: string;
  taxNumber: string;
  companyNumber: string;
  address: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  stateCode: string;
  postalCode: string;
  country: string;
  countryCode: string;
  clientCode: string;
  clientGroupCode: string;
}

export function CsvClientMapFields({
  file,
  onSubmitted,
  fileKey,
}: MapCSVClientFieldsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<CSVMapClientFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const formValues = useWatch({ control });

  const [loading, setLoading] = React.useState(false);

  const {
    successedRow,
    failedRowDetails,
    failedCount,
    warningCount,
    fieldNames,
  } = useCsvClientValidation({ file, fields: formValues });
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const suppressInvite = true;

  React.useEffect(() => {
    let triggerValidation = false;
    if (fieldNames?.length > 0) {
      const defaultFields: Record<string, string> = {
        firstName: 'First Name',
        lastName: 'Last Name',
        name: 'Entity Name',
        email: 'Email',
        mobile: 'Mobile',
        phone: 'Phone',
        taxNumber: 'ABN',
        companyNumber: 'ACN',
        address: 'Full Address',
        address1: 'Address 1',
        address2: 'Address 2',
        city: 'City',
        state: 'State',
        stateCode: 'State Code',
        postalCode: 'Postal Code',
        country: 'Country',
        countryCode: 'Country Code',
        clientCode: 'Client Code',
        clientGroupCode: 'Client Group Code',
      };

      Object.entries(defaultFields).forEach(([key, value]) => {
        if (fieldNames.includes(value)) {
          setValue(key as keyof CSVMapClientFormData, value);
        }

        if (fieldNames.includes(key)) {
          setValue(key as keyof CSVMapClientFormData, key);
        }

        if (!triggerValidation) {
          triggerValidation = true;
        }
      });

      if (triggerValidation) {
        trigger();
      }
    }
  }, [fieldNames, setValue, trigger]);

  function validateField(
    value: string | null | undefined,
    requiredMessage: string
  ) {
    return (
      (value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== 'Ignore column') ||
      requiredMessage
    );
  }

  const inputs = React.useMemo(
    () => ({
      firstName: {
        label: t('firstName', { ns: 'contacts' }),
        name: 'firstName' as const,
        type: 'text',
        placeholder: t('firstNamePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {
          validate: (value: string | null | undefined) =>
            validateField(value, t('firstNameRequired', { ns: 'contacts' })),
        },
      },
      lastName: {
        label: t('lastName', { ns: 'contacts' }),
        name: 'lastName' as const,
        type: 'text',
        placeholder: t('lastNamePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {
          validate: (value: string | null | undefined) =>
            validateField(value, t('lastNameRequired', { ns: 'contacts' })),
        },
      },
      name: {
        label: t('entityName', { ns: 'contacts' }),
        name: 'name' as const,
        type: 'text',
        placeholder: t('companyNamePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {
          validate: (value: string | null | undefined) =>
            validateField(value, t('companyNameRequired', { ns: 'contacts' })),
        },
      },
      email: {
        label: t('email', { ns: 'contacts' }),
        name: 'email' as const,
        type: 'email',
        defaultValue: '',
        placeholder: t('emailPlaceholder', { ns: 'contacts' }),
        rules: {},
      },
      mobile: {
        label: t('mobile', { ns: 'contacts' }),
        name: 'mobile' as const,
        type: 'text',
        placeholder: t('mobilePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
      phone: {
        label: t('phone', { ns: 'contacts' }),
        name: 'phone' as const,
        type: 'text',
        placeholder: t('phonePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
      taxNumber: {
        label: t('abn', { ns: 'contacts' }),
        name: 'taxNumber' as const,
        type: 'text',
        placeholder: t('taxNumberPlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
      companyNumber: {
        label: t('acn', { ns: 'contacts' }),
        name: 'companyNumber' as const,
        type: 'text',
        placeholder: t('companyNumberPlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
      address: {
        label: t('fullAddress', { ns: 'clients' }),
        name: 'address' as const,
        type: 'text',
        placeholder: t('addressPlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      address1: {
        label: t('address1', { ns: 'clients' }),
        name: 'address1' as const,
        type: 'text',
        placeholder: t('address1Placeholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      address2: {
        label: t('address2', { ns: 'clients' }),
        name: 'address2' as const,
        type: 'text',
        placeholder: t('address2Placeholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      city: {
        label: t('city', { ns: 'clients' }),
        name: 'city' as const,
        type: 'text',
        placeholder: t('cityPlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      state: {
        label: t('state', { ns: 'clients' }),
        name: 'state' as const,
        type: 'text',
        placeholder: t('statePlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      stateCode: {
        label: t('stateCode', { ns: 'clients' }),
        name: 'stateCode' as const,
        type: 'text',
        placeholder: t('stateCodePlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      postalCode: {
        label: t('postalCode', { ns: 'clients' }),
        name: 'postalCode' as const,
        type: 'text',
        placeholder: t('postalCodePlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      country: {
        label: t('country', { ns: 'clients' }),
        name: 'country' as const,
        type: 'text',
        placeholder: t('countryPlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      countryCode: {
        label: t('countryCode', { ns: 'clients' }),
        name: 'countryCode' as const,
        type: 'text',
        placeholder: t('countryCodePlaceholder', { ns: 'clients' }),
        defaultValue: '',
        rules: {},
      },
      clientCode: {
        label: t('clientCode', { ns: 'contacts' }),
        name: 'clientCode' as const,
        type: 'text',
        placeholder: t('clientCodePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
      clientGroupCode: {
        label: t('clientGroupCode', { ns: 'contacts' }),
        name: 'clientGroupCode' as const,
        type: 'text',
        placeholder: t('clientGroupCodePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
    }),
    [t]
  );

  const [createClientBulkUpload, { error: createClientError }] = useMutation(
    gql`
      ${CREATE_CLIENT_BULK_UPLOAD}
    `
  );

  const { data: selectedEntityIdData } = useQuery(gql(GET_SELECTED_ENTITY_ID));
  const entityId = selectedEntityIdData?.selectedEntityId;

  const onSubmit = async (data: CSVMapClientFormData) => {
    if (successedRow === 0) return;
    setLoading(true);
    try {
      const bulkImportInput: CreateClientBulkImportInput = {
        entityId,
        fileKey,
        suppressInvite,
        fields: {
          firstName:
            data.firstName && data.firstName !== 'Ignore column'
              ? data.firstName
              : '',
          lastName:
            data.lastName && data.lastName !== 'Ignore column'
              ? data.lastName
              : '',
          phone: data.phone && data.phone !== 'Ignore column' ? data.phone : '',
          mobile:
            data.mobile && data.mobile !== 'Ignore column' ? data.mobile : '',
          email: data.email && data.email !== 'Ignore column' ? data.email : '',
          name: data.name && data.name !== 'Ignore column' ? data.name : '',
          taxNumber:
            data.taxNumber && data.taxNumber !== 'Ignore column'
              ? data.taxNumber
              : '',
          companyNumber:
            data.companyNumber && data.companyNumber !== 'Ignore column'
              ? data.companyNumber
              : '',
          address:
            data.address && data.address !== 'Ignore column'
              ? data.address
              : '',
          address1:
            data.address1 && data.address1 !== 'Ignore column'
              ? data.address1
              : '',
          address2:
            data.address2 && data.address2 !== 'Ignore column'
              ? data.address2
              : '',
          stateCode:
            data.stateCode && data.stateCode !== 'Ignore column'
              ? data.stateCode
              : '',
          city: data.city && data.city !== 'Ignore column' ? data.city : '',
          state: data.state && data.state !== 'Ignore column' ? data.state : '',
          postalCode:
            data.postalCode && data.postalCode !== 'Ignore column'
              ? data.postalCode
              : '',
          country:
            data.country && data.country !== 'Ignore column'
              ? data.country
              : '',
          countryCode:
            data.countryCode && data.countryCode !== 'Ignore column'
              ? data.countryCode
              : '',
          clientCode:
            data.clientCode && data.clientCode !== 'Ignore column'
              ? data.clientCode
              : '',
          clientGroupCode:
            data.clientGroupCode && data.clientGroupCode !== 'Ignore column'
              ? data.clientGroupCode
              : '',
        },
      };
      console.log('bulkImportInput: ', bulkImportInput);

      await createClientBulkUpload({
        variables: {
          input: {
            ...bulkImportInput,
          },
        },
      });

      setLoading(false);
      setSubmitted(true);
      onSubmitted && onSubmitted();
    } catch (err) {
      console.log(`error create clients bulk upload: `, err);
      setLoading(false);
    }
  };
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <WBFlex width={'100%'} justifyContent={'center'}>
      <WBFlex
        flexDirection="column"
        alignItems="center"
        width={{
          xs: '100%',
          sm: '80%',
          md: '70%',
          lg: '60%',
        }}
      >
        <WBForm
          onSubmit={handleSubmit(onSubmit)}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          alignSelf="stretch"
          mt={[1, 6, 8]}
        >
          <WBTypography
            component={'div'}
            align="center"
            fontWeight={'regular'}
            textAlign={'center'}
            justifyContent={'center'}
          >
            {t('mapCSVFileDesciption', { ns: 'contacts' })}
          </WBTypography>
          <WBTableContainer sx={{ mt: 5 }}>
            <WBTable>
              <WBTableHead
                sx={{ color: 'white', bgcolor: 'black', border: 'none' }}
              >
                <WBTableRow>
                  <WBTableCell sx={{ width: '40%' }}>
                    {t('clientField', { ns: 'contacts' })}
                  </WBTableCell>
                  <WBTableCell sx={{ width: '10%' }} />
                  <WBTableCell sx={{ width: '50%' }}>
                    {t('yourCSVFileField', { ns: 'contacts' })}
                  </WBTableCell>
                </WBTableRow>
              </WBTableHead>
              <WBTableBody>
                {Object.entries(inputs).map(([key, input]) => (
                  <WBTableRow key={key}>
                    <WBTableCell>{input.label}</WBTableCell>
                    <WBTableCell>
                      <WBIcon name="ArrowForward" size={'medium'}></WBIcon>
                    </WBTableCell>
                    <WBTableCell>
                      <Controller
                        control={control}
                        name={key as any}
                        rules={input.rules}
                        defaultValue={input.defaultValue}
                        render={({ field }) => (
                          <FormControl
                            sx={{
                              backgroundColor: theme.palette.common.white,
                              height: '70%',
                              width: '90%',
                              fontSize: theme.typography.body1,
                              fontWeight: 'bold',
                            }}
                          >
                            <WBSelect
                              value={field.value}
                              SelectProps={{
                                SelectDisplayProps: {
                                  style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    color:
                                      field.value === 'Ignore column'
                                        ? 'grey'
                                        : 'initial',
                                    fontWeight: 'medium',
                                  },
                                },
                              }}
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  borderStyle: 'dotted',
                                  borderWidth: '2px',
                                  paddingX: '12px',
                                  fontWeight: 'medium',
                                  color: 'grey',
                                },
                              }}
                              helperText={
                                errors[field.name as keyof CSVMapClientFormData]
                                  ?.message ?? ''
                              }
                              error={
                                !!errors[
                                  field.name as keyof CSVMapClientFormData
                                ]
                              }
                            >
                              {['Ignore column', ...fieldNames].map(
                                (fieldName, index) => (
                                  <WBMenuItem
                                    key={index}
                                    value={fieldName}
                                    onClick={() => {
                                      field.onChange(fieldName);
                                      trigger(field.name);
                                    }}
                                  >
                                    <WBTypography
                                      noWrap
                                      fontWeight="medium"
                                      color={index !== 0 ? 'inherit' : 'grey'}
                                    >
                                      {`${fieldName}`}
                                    </WBTypography>
                                  </WBMenuItem>
                                )
                              )}
                            </WBSelect>
                          </FormControl>
                        )}
                      />
                    </WBTableCell>
                  </WBTableRow>
                ))}
              </WBTableBody>
            </WBTable>
          </WBTableContainer>
          {isValid && failedCount > 0 ? (
            <WBTypography color={'error'} mt={1}>
              <WBTypography
                component={'span'}
                color={'inherit'}
                fontWeight="bold"
                variant="inherit"
              >
                {failedCount}{' '}
              </WBTypography>
              {`${t('notImported', { ns: 'contacts' })}`}{' '}
            </WBTypography>
          ) : null}{' '}
          {isValid && warningCount > 0 ? (
            <WBTypography color={'warning.main'}>
              <WBTypography
                component={'span'}
                color={'inherit'}
                fontWeight="bold"
                variant="inherit"
              >
                {`${warningCount}`}
              </WBTypography>{' '}
              {`${t('partiallyImported', { ns: 'contacts' })}`}
            </WBTypography>
          ) : null}
          {isValid && (failedCount > 0 || warningCount > 0) && (
            <WBLink
              fontWeight={'bold'}
              underline="always"
              onClick={handleClickOpen}
            >
              {t('viewReport', { ns: 'contacts' })}
            </WBLink>
          )}
          <WBFlex mt={[5, 6, 7]} flexDirection="column" alignItems="center">
            <ErrorHandler errorMessage={createClientError?.message} />
            {!submitted ? (
              <ButtonGroup
                variant="outlined"
                aria-label="split button"
                disableRipple
                disableFocusRipple
                disableElevation
              >
                <WBButton
                  type="submit"
                  sx={{
                    paddingX: loading ? 9 : 6,
                    marginRight: 2,
                    marginBottom: 5,
                  }}
                  loading={loading}
                  disabled={!isValid}
                >
                  {`${t('importClients', {
                    ns: 'contacts',
                  })} (${successedRow})`}
                </WBButton>
                {/*{!loading ? (*/}
                {/*  <WBButton disabled={!isValid}>*/}
                {/*    <WBIcon*/}
                {/*      name="ChevronDown"*/}
                {/*      size={1.5}*/}
                {/*      color="inhreit"*/}
                {/*    ></WBIcon>*/}
                {/*  </WBButton>*/}
                {/*) : null}*/}
              </ButtonGroup>
            ) : (
              <WBBox
                sx={{
                  borderRadius: 20,
                  padding: 2,
                  paddingX: 6,
                  bgcolor: theme.palette.success.main,
                  fontWeight: 'bold',
                  color: theme.palette.common.black,
                }}
              >
                {t('importingClients', { ns: 'contacts' })}
              </WBBox>
            )}
          </WBFlex>
        </WBForm>
      </WBFlex>
      <WBDialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        PaperComponent={Paper}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: { p: 4 },
        }}
      >
        <WBIconButton
          aria-label={t('close', { ns: 'common' })}
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: (theme) => theme.spacing(4),
            top: (theme) => theme.spacing(4),
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <WBIcon name="Close" />
        </WBIconButton>
        <DialogTitle variant="h3" id="scroll-dialog-title">
          {t('bulkImportReport', { ns: 'contacts' })}
        </DialogTitle>
        <WBDialogContent dividers>
          <WBTableContainer component={WBBox}>
            <WBTable aria-label="simple table">
              <WBTableBody>
                {failedRowDetails.map((row, index) => (
                  <WBTableRow
                    key={index}
                    sx={{
                      borderBottom: 1,
                      borderColor: theme.palette.grey[400],
                      fontWeight: 'regular',
                    }}
                  >
                    <WBTableCell
                      component="th"
                      scope="row"
                      sx={{
                        fontWeight: 'inherit',
                        textWrap: 'nowrap',
                        px: 0,
                        py: 3,
                      }}
                    >
                      {`Row ${row.no + 1}`}
                    </WBTableCell>
                    <WBTableCell align="right" sx={{ fontWeight: 'inherit' }}>
                      {row.value}
                    </WBTableCell>
                    <WBTableCell
                      align="right"
                      sx={{
                        color:
                          row.type === 'error' ? 'error.main' : 'warning.main',
                        fontWeight: 'inherit',
                        px: 0,
                      }}
                    >
                      {row.message}
                    </WBTableCell>
                  </WBTableRow>
                ))}
              </WBTableBody>
            </WBTable>
          </WBTableContainer>
        </WBDialogContent>
      </WBDialog>
      ;
    </WBFlex>
  );
}
